/**
 * Error boundary (XC-04). A render error in a screen — say, reading a field an API response left
 * null — shows a recoverable message instead of unmounting the whole app to a blank page.
 *
 * App.tsx keys the route-level boundary by pathname, so moving to another page clears the error;
 * main.tsx keeps an outer one for anything outside the routes.
 */

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("render_error", error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <main
        role="alert"
        className="flex min-h-[60dvh] flex-col items-center justify-center gap-4 px-4 text-center text-ink"
      >
        <h1 className="page-title">Something went wrong</h1>
        <p className="max-w-sm text-sm text-ink-muted">
          This page hit an unexpected problem. Reloading usually fixes it — nothing you saved is lost.
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
          <button type="button" className="btn-secondary" onClick={() => window.history.back()}>
            Go back
          </button>
        </div>
      </main>
    );
  }
}
